/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { CardCategory } from '@coursera/cds-core';
import type { ProductCard as ProductCardTrackingData, Recommender } from '@coursera/event-pulse-types';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardBadge from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import type { ImageParams } from 'bundles/product-card/components/LearningProductCardImage';
import ProductCardCourseraPlus from 'bundles/product-card/components/legacy/ProductCardCourseraPlus';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { AUTOCOMPLETE_PHOTO_SIZE, CourseIcon } from 'bundles/search-common/constants/autocomplete';
import { saveRecentlyViewed } from 'bundles/search-common/utils/SearchUtils';

const LAZY_LOAD_THRESHOLD = 5;

// Helper function to convert string difficulty level to enum type
const convertDifficultyToEnum = (difficulty: string | undefined): DifficultyLevel | null => {
  if (!difficulty) return null;
  const upperCase = difficulty.toUpperCase();
  // Valid enum values: BEGINNER, INTERMEDIATE, ADVANCED, MIXED
  if (upperCase === 'BEGINNER' || upperCase === 'INTERMEDIATE' || upperCase === 'ADVANCED' || upperCase === 'MIXED') {
    return upperCase as DifficultyLevel;
  }
  return null;
};

type Props = {
  id?: string;
  name?: string;
  slug?: string;
  objectUrl?: string;
  imageUrl?: string;
  imageProps?: ImageParams;
  productType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];
  productDifficultyLevel?: string;
  productDuration?: string;
  partners?: { id: string; logo?: string; name: string }[];
  partnerLogos?: string[];
  statusTags?: string[] | null;
  coBrandingEnabled?: boolean;
  skills?: string[];
  avgProductRatings?: number;
  numProductRatings?: number;
  isNewContent?: boolean;
  isPartOfCourseraPlus?: boolean;
  ownsCourseraPlus?: boolean;
  ownsCourseraLite?: boolean;
  cardPosition?: number;
  shouldPrioritizeImageLoading?: boolean;
  forceOpenInNewTab?: boolean;
  variant?: 'list' | 'grid';
  recommender?: Recommender;
  indexName?: string;
  trackedLinkProps?: {
    trackingName: string;
    withVisibilityTracking: boolean;
    requireFullyVisible: boolean;
    data: Record<string, unknown>;
    refAlt?: React.RefObject<HTMLAnchorElement>;
  };
  additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
  searchedQuery?: string;
  isPathwayContent?: boolean;
};

const SearchCard = ({
  id,
  name,
  imageUrl,
  imageProps,
  objectUrl,
  slug,
  productType,
  productDifficultyLevel,
  productDuration,
  partners,
  partnerLogos,
  coBrandingEnabled = false,
  skills,
  avgProductRatings,
  numProductRatings,
  isNewContent,
  isPartOfCourseraPlus,
  ownsCourseraLite,
  ownsCourseraPlus,
  statusTags,
  cardPosition,
  shouldPrioritizeImageLoading,
  forceOpenInNewTab,
  variant,
  recommender,
  indexName,
  trackedLinkProps,
  additionalProductCardTrackingData,
  searchedQuery,
  isPathwayContent,
}: Props) => {
  const handleCardClick = React.useCallback<() => void>(() => {
    if (!name || !objectUrl) {
      return;
    }

    // Save to recently viewed when card is clicked
    // This will merge with any existing entry by name (handled by saveRecentlyViewed)
    saveRecentlyViewed({
      id,
      name,
      slug,
      path: objectUrl,
      imageIconWithSize: imageUrl ? { imageUrl, size: AUTOCOMPLETE_PHOTO_SIZE } : CourseIcon,
      cardImageUrl: imageUrl,
      partnerName: partners && partners[0]?.name,
      partnerLogo: partnerLogos && partnerLogos[0],
      difficulty: convertDifficultyToEnum(productDifficultyLevel),
      productType: productType || null,
      isPathwayProduct: isPathwayContent || false,
    });
  }, [
    id,
    name,
    slug,
    objectUrl,
    imageUrl,
    partners,
    partnerLogos,
    productDifficultyLevel,
    productType,
    isPathwayContent,
  ]);

  // Create a wrapper component that calls handleCardClick on click
  const TrackedLinkWithSave = React.useCallback<React.ComponentType<React.ComponentProps<typeof TrackedLink2>>>(
    (props) => {
      const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        handleCardClick();
        props.onClick?.(e);
      };
      return <TrackedLink2 {...props} onClick={handleClick} />;
    },
    [handleCardClick]
  );

  return (
    <LearningProductCard
      ImageComponent={
        <LearningProductCardImage
          imageUrl={imageUrl || ''}
          enableFluidWidthForLazyImg={true}
          prioritizeImageLoading={shouldPrioritizeImageLoading}
          enableLazyLoading={cardPosition ? cardPosition > LAZY_LOAD_THRESHOLD : true}
          imageParams={{ ...imageProps, ...getImagePropOverrides(partners ?? [], imageUrl ?? '') }}
        />
      }
      linkProps={{
        href: objectUrl,
        target: forceOpenInNewTab ? '_blank' : undefined,
        component: TrackedLinkWithSave,
      }}
      badge={
        <>
          <CardCategory>
            <ProductCardCourseraPlus
              isPartOfCourseraPlus={isPartOfCourseraPlus ?? false}
              ownsCourseraLite={ownsCourseraLite ?? false}
              ownsCourseraPlus={ownsCourseraPlus ?? false}
              productType={productType}
            />
          </CardCategory>
          <LearningProductCardBadge
            customBadge={isPathwayContent ? <PathwayContentBadge /> : undefined}
            productType={productType}
          />
        </>
      }
      partners={partners && getPartnerNamesAndLogos(partners, coBrandingEnabled, partnerLogos)}
      skills={skills}
      numProductRatings={numProductRatings}
      avgProductRating={avgProductRatings}
      productType={productType}
      productDifficultyLevel={productDifficultyLevel}
      productDuration={productDuration}
      statusTags={getProductCardStatusTags({ isNew: isNewContent, statusTags, slug })}
      id={id}
      name={name || ''}
      slug={slug}
      variant={variant}
      cardIndex={cardPosition}
      indexName={indexName}
      recommender={recommender}
      trackingProps={{
        trackedLinkProps,
        additionalV3TrackingData: { searchedQuery },
        additionalProductCardTrackingData,
      }}
    />
  );
};

export default SearchCard;
